import {
  AudioHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import useMayHearAudio from "../../lib/useMayHearAudio";

type PropsType = Omit<AudioHTMLAttributes<HTMLAudioElement>, "muted"> & {
  srcObject?: MediaStream;
  onAudioBlocked?: () => void;
  onAudioUnblocked?: () => void;
};

export default function ParticipantAudio({
  srcObject,
  onAudioBlocked,
  onAudioUnblocked,
  ...props
}: PropsType) {
  const refAudio = useRef<HTMLAudioElement>(null);
  const [pendingPlay, setPendingPlay] = useState(false);
  // If we add these as deps to tryPlay, inline callbacks from the parent would
  // cause tryPlay to change on every render, re-running the srcObject effect
  // and calling play() even when only the remote mutes their microphone.
  const onAudioBlockedRef = useRef(onAudioBlocked);
  const onAudioUnblockedRef = useRef(onAudioUnblocked);
  onAudioBlockedRef.current = onAudioBlocked;
  onAudioUnblockedRef.current = onAudioUnblocked;

  const tryPlay = useCallback(() => {
    if (!refAudio.current || !refAudio.current.srcObject) return;

    refAudio.current
      .play()
      .then(() => {
        setPendingPlay(false);
        onAudioUnblockedRef.current?.();
      })
      .catch(() => {
        setPendingPlay(true);
        onAudioBlockedRef.current?.();
      });
  }, []);

  useEffect(() => {
    if (!refAudio.current) return;

    refAudio.current.srcObject = srcObject as MediaProvider;

    if (srcObject) {
      tryPlay();
    }
  }, [srcObject, tryPlay]);

  // Fallback: on the first user gesture anywhere on the page,
  // retry play() if it was previously blocked by autoplay policy.
  // After a refresh the browser requires a user interaction before
  // allowing audio playback.
  useEffect(() => {
    if (!pendingPlay) return;

    const handleUserGesture = () => tryPlay();

    document.addEventListener("click", handleUserGesture);
    document.addEventListener("keydown", handleUserGesture);
    document.addEventListener("touchstart", handleUserGesture);

    return () => {
      document.removeEventListener("click", handleUserGesture);
      document.removeEventListener("keydown", handleUserGesture);
      document.removeEventListener("touchstart", handleUserGesture);
    };
  }, [pendingPlay, tryPlay]);

  const mayHearAudio = useMayHearAudio();

  // Enforce headset-only audio on the Ameelio tablet to prevent speaker bleed.
  useEffect(() => {
    if (refAudio.current) refAudio.current.muted = !mayHearAudio;
  }, [mayHearAudio]);

  return <audio ref={refAudio} {...props} />;
}
